import React, { useContext, useEffect, useState } from 'react';
import { SentryContext } from './SentryProvider';
import { PrimaryButton, SecondaryButton } from '../connectedForm/Button';
import { BasicHeader } from '../../actions/BasicHeader';
import BlurTextField from '../connectedForm/BlurTextField';
import { ModalBottomButtonContainer } from '../modals/Styles';
import * as Sentry from '@sentry/react';
import { BlurTextFieldType } from '../connectedForm/BlurTextField/BlurTextField';
import { useFullUserDetails } from '../../utils/hooks';
import { serviceActions } from '../../state/modules/service';
import { useDispatch } from 'react-redux';
import { AlertType } from '../../graphql/schema/p4types';
import { ModalContentContainer, ModalContentHeaderContainer } from '../modals/ModalComponents';
import FluentModal from '../modals/FluentModal';

type SentryUserFeedbackModalPropsType = {
  errorId?: string;
};

export const SentryUserFeedbackModal = ({ errorId }: SentryUserFeedbackModalPropsType) => {
  const { lastEventId } = useContext(SentryContext);
  const sentryContextEventId = lastEventId?.current;

  const fullUserDetails = useFullUserDetails();

  const [modalOpen, setModalOpen] = useState(false);
  const [userFeedbackName, setUserFeedbackName] = useState(fullUserDetails?.systemUser?.username ?? undefined);
  const [userFeedbackEmail, setUserFeedbackEmail] = useState(fullUserDetails?.systemUser?.email ?? undefined);
  const [userFeedbackWhatHappened, setUserFeedbackWhatHappened] = useState('');
  const [userFeedbackEventId, setUserFeedbackEventId] = useState(errorId);
  const [issueReported, setIssueReported] = useState(false);

  useEffect(() => {
    setUserFeedbackName(fullUserDetails?.systemUser?.username ?? undefined);
  }, [fullUserDetails?.systemUser?.username]);

  useEffect(() => {
    setUserFeedbackEmail(fullUserDetails?.systemUser?.email ?? undefined);
  }, [fullUserDetails?.systemUser?.email]);

  useEffect(() => {
    if (errorId) {
      setUserFeedbackEventId(errorId);
    }
  }, [errorId]);

  const dispatch = useDispatch();

  return (
    <>
      <SecondaryButton disabled={issueReported} onClick={() => setModalOpen(true)} inline>
        {!issueReported && 'Add Detail'} {issueReported && 'OGL has received your report'}
      </SecondaryButton>
      <FluentModal isOpen={modalOpen} styles={{ main: { paddingBottom: '60px', minWidth: '600px' } }}>
        <BasicHeader>Add Detail</BasicHeader>
        <ModalContentContainer>
          <ModalContentHeaderContainer>
            <h5>
              Please provide any additional information which you feel may be helpful in resolving this issue.
              Submitting additional detail <br /> does not raise a support incident. Please contact Profit4 support if
              you would like an incident raised.
            </h5>
          </ModalContentHeaderContainer>
          <BlurTextField
            label={'Contact Name'}
            labelCols={1 / 4}
            inputCols={3 / 4}
            data-testid={'sentry-name-text-field'}
            value={userFeedbackName}
            enableOnChange={true}
            onChange={(value: React.SetStateAction<string | undefined>) => {
              setUserFeedbackName(value);
            }}
          />
          <BlurTextField
            label={'Contact Email'}
            labelCols={1 / 4}
            inputCols={3 / 4}
            data-testid={'sentry-email-text-field'}
            value={userFeedbackEmail}
            enableOnChange={true}
            onChange={(value: React.SetStateAction<string | undefined>) => {
              setUserFeedbackEmail(value);
            }}
          />
          <BlurTextField
            label="What happened?"
            type={BlurTextFieldType.TextArea}
            labelCols={1 / 4}
            inputCols={3 / 4}
            data-testid={'sentry-what-happened-text-field'}
            value={userFeedbackWhatHappened}
            enableOnChange={true}
            placeholder={
              'Please describe what you were doing when the error occurred. Include any steps that led to the issue and any relevant details that might help us diagnose the problem.'
            }
            onChange={(value: React.SetStateAction<string>) => {
              setUserFeedbackWhatHappened(value);
            }}
          />
        </ModalContentContainer>
        <ModalBottomButtonContainer>
          <SecondaryButton
            onClick={() => {
              setModalOpen(false);
            }}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={() => {
              const userFeedback = {
                name: userFeedbackName,
                email: userFeedbackEmail,
                message: userFeedbackWhatHappened,
                associatedEventId: userFeedbackEventId || sentryContextEventId,
              };

              Sentry.captureFeedback(userFeedback);
              setIssueReported(true);

              dispatch(
                serviceActions.genericAlert(
                  'OGL has received your report',
                  'Thank you for providing additional detail. Note that this does not raise a support incident, ' +
                    'please contact the Profit4 team if you would like an incident raised.',
                  AlertType.Info
                )
              );

              setModalOpen(false);
            }}
          >
            Add Detail
          </PrimaryButton>
        </ModalBottomButtonContainer>
      </FluentModal>
    </>
  );
};
